import 'pdfjs-dist/web/pdf_viewer.css';
import '@zonuexe/pdf.js-controller/css/pdf-slide.css';
import '../css/pdf-slide.css';
import PDFController, { Events } from '@zonuexe/pdf.js-controller';
import { throttle } from 'es-toolkit';
import workerSrc from 'pdfjs-dist/build/pdf.worker.mjs?url';
import elephpantUrl from '../images/elephpant-mirrored.svg?url';
import turtleUrl from '../images/mini-kame-taro.png?url';
import { drawLaserTrail, pruneLaserTrail, type LaserPoint } from './laser';

function requireHtmlElement(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!(element instanceof HTMLElement)) {
    throw new Error(`Element with id "${id}" was not found`);
  }
  return element;
}

const params = new URLSearchParams(window.location.search);
const pdfUrlParam = params.get('slide');
const isFinePointer = window.matchMedia('(pointer: fine)').matches;

if (!pdfUrlParam) {
  alert('Please open this page with ?slide=<PDF URL>');
  throw new Error('Missing slide query parameter');
}

const slideUrl: string = pdfUrlParam;
const container = requireHtmlElement('pdf-container');

let controlsElement: HTMLElement | null = null;

const eventRegistry = new AbortController();
const { signal: eventSignal } = eventRegistry;

const DEFAULT_RABBIT_TURTLE_STEP_MS = 5000;
const RABBIT_SPRITE_SOURCES = {
  elephpant: elephpantUrl,
  turtle: turtleUrl,
} as const;
type RabbitSpriteType = keyof typeof RABBIT_SPRITE_SOURCES;
const DEFAULT_RABBIT_SPRITE: RabbitSpriteType = 'elephpant';

type RabbitTimerDomRefs = {
  root: HTMLElement;
  track: HTMLElement;
  turtle: HTMLImageElement;
  bunny: HTMLImageElement;
  status: HTMLElement | null;
  elapsedFlag: HTMLElement;
  totalFlag: HTMLElement;
};

type RabbitTimerSpeakerState = 'idle' | 'running' | 'completed' | 'error';

type RabbitTimerStatusExtras = {
  minutes?: number;
  intervalSeconds?: number;
  elapsedMs?: number;
  durationMs?: number;
  sprite?: RabbitSpriteType;
};

type RabbitTimerState = {
  dom: RabbitTimerDomRefs | null;
  durationMs: number;
  startTimestamp: number | null;
  intervalId: number | null;
  isRunning: boolean;
  hasStarted: boolean;
  lastTimeRatio: number;
  lastPageRatio: number;
  turtleStepMs: number;
  spriteType: RabbitSpriteType;
};

const rabbitTimerState: RabbitTimerState = {
  dom: null,
  durationMs: 0,
  startTimestamp: null,
  intervalId: null,
  isRunning: false,
  hasStarted: false,
  lastTimeRatio: -1,
  lastPageRatio: -1,
  turtleStepMs: DEFAULT_RABBIT_TURTLE_STEP_MS,
  spriteType: DEFAULT_RABBIT_SPRITE,
};

// ===== Attention tools (laser / pen / highlighter / spotlight / blank) =====
type InkToolType = 'pen' | 'highlighter';
type NormPoint = { x: number; y: number };
type InkStroke = { tool: InkToolType; color: string; size: number; points: NormPoint[] };
type BlankMode = 'off' | 'black' | 'white';
type AnnotationDrawMessage = {
  tool?: 'laser' | 'pen' | 'highlighter' | 'spotlight';
  phase?: 'start' | 'move' | 'end';
  x?: number;
  y?: number;
  color?: string;
  size?: number;
};

const DEFAULT_PEN_COLOR = '#ff2d55';
const DEFAULT_PEN_SIZE = 0.005; // fraction of slide width
const DEFAULT_HIGHLIGHTER_SIZE = 0.03;
const SPOTLIGHT_RADIUS_RATIO = 0.18; // fraction of slide diagonal
const SPOTLIGHT_DIM = 0.62;

type AnnotationState = {
  overlay: HTMLCanvasElement | null;
  ctx: CanvasRenderingContext2D | null;
  blankLayer: HTMLElement | null;
  strokesByPage: Map<number, InkStroke[]>;
  activeStroke: InkStroke | null;
  laserTrail: LaserPoint[];
  spotlight: NormPoint | null;
  rafId: number | null;
  blankMode: BlankMode;
};

const annotationState: AnnotationState = {
  overlay: null,
  ctx: null,
  blankLayer: null,
  strokesByPage: new Map(),
  activeStroke: null,
  laserTrail: [],
  spotlight: null,
  rafId: null,
  blankMode: 'off',
};

let speakerWindowRef: Window | null = null;
let speakerHandshakeToken: string | null = null;
let speakerChannelRegistered = false;
let pendingSpeakerWindow: Window | null = null;
let pendingSpeakerReadyTimeout: number | null = null;

window.addEventListener(
  'pagehide',
  () => {
    eventRegistry.abort();
  },
  { once: true },
);

eventSignal.addEventListener('abort', () => {
  closeSpeakerWindow();
  if (rabbitTimerState.intervalId !== null) {
    window.clearInterval(rabbitTimerState.intervalId);
    rabbitTimerState.intervalId = null;
  }
});

window.addEventListener(
  'beforeunload',
  () => {
    closeSpeakerWindow();
  },
  { signal: eventSignal },
);

window.addEventListener(
  'message',
  (event) => {
    handleSpeakerWindowMessage(event);
  },
  { signal: eventSignal },
);

const controller = new PDFController({
  container,
  workerSrc,
  cMapUrl: `${import.meta.env.BASE_URL}cmaps/`,
  cMapPacked: true,
  doubleBuffer: true,
  willReadFrequently: true,
  pageNumber: parsePageFromHash() ?? 1,
});

bootstrap().catch((error) => {
  console.error(error);
  alert('Failed to load the PDF document. Check the console for details.');
});

async function bootstrap() {
  await controller.loadDocument(slideUrl);
  await initializeNavigation();
}

async function initializeNavigation() {
  attachColourSync();
  await goToPageFromAnchor();
  wireNavigationControls();
  wireClickNavigationZones();
  wireSpeakerViewControl();
  wireKeyboardShortcuts();
  wireResizeHandler();
  wireSwipeGestures();
  setupControlsVisibility();
  setupRabbitTimerControls();
  setupAnnotationOverlay();
  setupHostFullscreen();
  // 初期表示時は常にSpeakerボタンを表示
  showSpeakerButton();
}

function attachColourSync() {
  container.addEventListener(
    Events.page_rendered,
    () => {
      updateColours();
    },
    { signal: eventSignal },
  );
}

function updateColours() {
  const context: CanvasRenderingContext2D | null = controller.canvasContext ?? null;
  if (!context) {
    return;
  }
  const cornerColor = getCornerColor(context);
  container.style.backgroundColor = cornerColor;
  document.body.style.backgroundColor = cornerColor;
}

function getCornerColor(context: CanvasRenderingContext2D): string {
  const canvasColor = context.getImageData(0, 0, 1, 1);
  const [r, g, b] = canvasColor.data;
  return `rgb(${r},${g},${b})`;
}

function parsePageFromHash(hash = window.location.hash): number | undefined {
  if (!hash.startsWith('#p=')) {
    return undefined;
  }
  const pageNum = Number.parseInt(hash.substring(3), 10);
  if (!Number.isFinite(pageNum) || pageNum <= 0) {
    return undefined;
  }
  return pageNum;
}

async function goToPageFromAnchor() {
  const pageNum = parsePageFromHash();
  if (pageNum === undefined) {
    postNavigationUpdate();
    return;
  }
  const totalPages = controller.numPages;
  const targetPage = Math.min(pageNum, Math.max(totalPages, 1));
  if (targetPage !== controller.pageNumber) {
    await jumpToPage(targetPage);
  }
  postNavigationUpdate();
}

async function jumpToPage(targetPage: number): Promise<void> {
  const last = Math.max(1, Math.min(targetPage, controller.numPages || 1));
  let pending: Promise<void> | undefined;
  while (controller.pageNumber < last) {
    pending = controller.nextPage();
  }
  while (controller.pageNumber > last) {
    pending = controller.prevPage();
  }
  if (pending) {
    await pending;
  }
}

function wireNavigationControls() {
  const prevButton = document.getElementById('js-prev');
  const nextButton = document.getElementById('js-next');

  prevButton?.addEventListener(
    'click',
    () => {
      showControls();
      void prevPage(false);
    },
    { signal: eventSignal },
  );
  nextButton?.addEventListener(
    'click',
    () => {
      showControls();
      void nextPage(false);
    },
    { signal: eventSignal },
  );

  window.addEventListener(
    'hashchange',
    () => {
      void goToPageFromAnchor();
    },
    { signal: eventSignal },
  );
}

function wireClickNavigationZones() {
  const zoneThreshold = 0.15;
  container.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented) {
        return;
      }
      const rect = container.getBoundingClientRect();
      if (rect.width <= 0) {
        return;
      }
      const xRatio = (event.clientX - rect.left) / rect.width;
      if (xRatio <= zoneThreshold) {
        event.preventDefault();
        void prevPage();
      } else if (xRatio >= 1 - zoneThreshold) {
        event.preventDefault();
        void nextPage();
      }
    },
    { signal: eventSignal },
  );
}

function wireSpeakerViewControl() {
  const button = document.getElementById('js-speaker');
  if (!(button instanceof HTMLElement)) {
    return;
  }
  if (!isFinePointer) {
    button.style.display = 'none';
    return;
  }
  button.addEventListener(
    'click',
    () => {
      showControls();
      openSpeakerView();
    },
    { signal: eventSignal },
  );
}

function wireKeyboardShortcuts() {
  document.addEventListener(
    'keydown',
    (event) => {
      if (handleNavigationKey(event)) {
        event.preventDefault();
        showControls();
      }
    },
    { signal: eventSignal },
  );
}

function handleNavigationKey(event: KeyboardEvent): boolean {
  if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
    return false;
  }
  switch (event.key) {
    case 'ArrowLeft':
    case 'ArrowDown':
    case 'k':
    case 'a':
    case 'K':
    case 'A':
    case 'PageUp':
      void prevPage();
      return true;
    case 'ArrowRight':
    case 'ArrowUp':
    case 'j':
    case 's':
    case 'J':
    case 'S':
    case 'PageDown':
      void nextPage();
      return true;
    default:
      return false;
  }
}

function openSpeakerView(): void {
  if (speakerWindowRef && !speakerWindowRef.closed) {
    speakerWindowRef.focus();
    scheduleSpeakerViewUpdate();
    sendSpeakerPing('refocus');
    return;
  }
  const handshake = generateSpeakerHandshake();
  speakerHandshakeToken = handshake;
  const speakerUrl = new URL('./speaker.html', window.location.href);
  speakerUrl.searchParams.set('speaker', '1');
  speakerUrl.searchParams.set('slide', slideUrl);
  speakerUrl.searchParams.set('handshake', handshake);
  const speakerWin = window.open(
    speakerUrl.toString(),
    'pdf-speaker-view',
    'width=1200,height=800',
  );
  if (!speakerWin) {
    speakerHandshakeToken = null;
    alert('ポップアップがブロックされているため、スピーカービューを開けません。');
    return;
  }
  pendingSpeakerWindow = speakerWin;
  if (pendingSpeakerReadyTimeout !== null) {
    window.clearTimeout(pendingSpeakerReadyTimeout);
  }
  pendingSpeakerReadyTimeout = window.setTimeout(() => {
    if (pendingSpeakerWindow === speakerWin) {
      console.error('Speaker view did not respond in time.');
      pendingSpeakerWindow = null;
      speakerHandshakeToken = null;
      resetSpeakerState();
      try {
        speakerWin.close();
      } catch {
        /* ignore close errors */
      }
    }
  }, 10000);
  try {
    speakerWin.focus();
  } catch {
    /* ignore focus errors */
  }
}

function generateSpeakerHandshake(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function handleSpeakerReady(source: MessageEventSource | null, handshake: string | undefined) {
  if (!source || typeof (source as Window).postMessage !== 'function') {
    return;
  }
  const normalizedHandshake = typeof handshake === 'string' ? handshake : undefined;
  if (!speakerHandshakeToken && normalizedHandshake) {
    speakerHandshakeToken = normalizedHandshake;
  }
  if (
    speakerHandshakeToken &&
    normalizedHandshake &&
    normalizedHandshake !== speakerHandshakeToken
  ) {
    console.warn('Ignoring speaker-ready with unexpected handshake value.');
    return;
  }
  const win = source as Window;
  if (pendingSpeakerWindow && win !== pendingSpeakerWindow) {
    console.warn('Received speaker-ready from unexpected window instance. Ignoring.');
    return;
  }
  if (speakerWindowRef === win && speakerChannelRegistered) {
    sendSpeakerAck(win);
    sendNavigationUpdate();
    notifySpeakerFullscreen();
    return;
  }
  if (pendingSpeakerReadyTimeout !== null) {
    window.clearTimeout(pendingSpeakerReadyTimeout);
    pendingSpeakerReadyTimeout = null;
  }
  pendingSpeakerWindow = null;
  speakerWindowRef = win;

  registerSpeakerCommunicationChannel();
  try {
    win.focus();
  } catch {
    /* ignore focus errors */
  }
  sendSpeakerAck(win);
  sendSpeakerPing('startup');
  sendNavigationUpdate();
  notifySpeakerFullscreen();
}

function sendSpeakerAck(target: Window) {
  if (!speakerHandshakeToken) {
    return;
  }
  try {
    target.postMessage(
      { type: 'speaker-ack', handshake: speakerHandshakeToken },
      window.location.origin,
    );
  } catch (error) {
    console.error('Failed to send speaker ack', error);
  }
}

function registerSpeakerCommunicationChannel() {
  if (speakerChannelRegistered) {
    return;
  }
  speakerChannelRegistered = true;
  sendSpeakerPing('channel-registered');
}

let fullscreenPrompt: HTMLButtonElement | null = null;

function isHostFullscreen(): boolean {
  return document.fullscreenElement !== null;
}

function setupHostFullscreen() {
  document.addEventListener(
    'fullscreenchange',
    () => {
      if (isHostFullscreen()) {
        hideFullscreenPrompt();
      }
      notifySpeakerFullscreen();
      void controller.fitItSize();
      syncOverlaySize();
      renderOverlay();
    },
    { signal: eventSignal },
  );
}

async function setHostFullscreen(on: boolean): Promise<void> {
  if (on) {
    if (isHostFullscreen()) {
      hideFullscreenPrompt();
      notifySpeakerFullscreen();
      return;
    }
    try {
      await document.documentElement.requestFullscreen();
      hideFullscreenPrompt();
      notifySpeakerFullscreen();
    } catch (error) {
      console.warn('Fullscreen request needs a click on the audience window', error);
      showFullscreenPrompt();
    }
    return;
  }
  hideFullscreenPrompt();
  if (isHostFullscreen()) {
    try {
      await document.exitFullscreen();
    } catch (error) {
      console.warn('Failed to exit fullscreen', error);
    }
  }
  notifySpeakerFullscreen();
}

function ensureFullscreenPrompt(): HTMLButtonElement {
  if (fullscreenPrompt) {
    return fullscreenPrompt;
  }
  const button = document.createElement('button');
  button.id = 'js-fullscreen-prompt';
  button.type = 'button';
  // The button covers the whole audience window so a click anywhere on the
  // slide counts; the pill label only tells the audience-side operator so.
  button.setAttribute('aria-label', '本体を全画面表示');
  const label = document.createElement('span');
  label.className = 'fullscreen-prompt__label';
  label.textContent = 'クリックすると全画面表示になります';
  button.appendChild(label);
  button.addEventListener(
    'click',
    () => {
      void setHostFullscreen(true);
    },
    { signal: eventSignal },
  );
  document.body.appendChild(button);
  fullscreenPrompt = button;
  return button;
}

function showFullscreenPrompt() {
  ensureFullscreenPrompt().style.display = 'block';
  notifySpeakerFullscreen(true);
}

function hideFullscreenPrompt() {
  if (fullscreenPrompt) {
    fullscreenPrompt.style.display = 'none';
  }
}

function notifySpeakerFullscreen(pendingPrompt = false) {
  if (!speakerWindowRef || speakerWindowRef.closed || !speakerHandshakeToken) {
    return;
  }
  const message = {
    type: 'host-fullscreen-status',
    handshake: speakerHandshakeToken,
    active: isHostFullscreen(),
    pendingPrompt: pendingPrompt && !isHostFullscreen(),
  } as const;
  try {
    speakerWindowRef.postMessage(message, getSpeakerOrigin());
  } catch (error) {
    console.warn('Failed to share fullscreen status with speaker view', error);
  }
}

function handleSpeakerWindowMessage(event: MessageEvent) {
  if (event.origin !== window.location.origin) {
    return;
  }
  const payload = event.data;
  if (!payload || typeof payload !== 'object') {
    return;
  }
  const data = payload as {
    type?: string;
    handshake?: string;
    label?: string;
    timestamp?: number;
    message?: string;
    key?: string;
    minutes?: number;
    intervalSeconds?: number;
    sprite?: string;
    tool?: 'laser' | 'pen' | 'highlighter' | 'spotlight';
    phase?: 'start' | 'move' | 'end';
    x?: number;
    y?: number;
    color?: string;
    size?: number;
    blankMode?: BlankMode;
    on?: boolean;
    page?: number;
  };

  if (data.type === 'speaker-ready') {
    handleSpeakerReady(event.source ?? null, data.handshake);
    return;
  }

  if (!speakerWindowRef || event.source !== speakerWindowRef) {
    return;
  }

  if (speakerHandshakeToken && data.handshake && data.handshake !== speakerHandshakeToken) {
    return;
  }

  switch (data.type) {
    case 'speaker-pong':
      console.debug(
        'Speaker view pong:',
        data.label ?? '',
        data.timestamp ? new Date(data.timestamp).toISOString() : '',
      );
      break;
    case 'speaker-log':
      console.debug('Speaker view log:', data.message ?? '');
      break;
    case 'speaker-ack':
      console.debug('Speaker view acknowledged handshake.');
      break;
    case 'speaker-keydown':
      // スピーカーウィンドウからのキーボードイベントをメインウィンドウに転送
      if (data.key) {
        const syntheticEvent = new KeyboardEvent('keydown', {
          key: data.key,
          bubbles: true,
          cancelable: true,
        });
        if (handleNavigationKey(syntheticEvent)) {
          syntheticEvent.preventDefault();
        }
      }
      break;
    case 'speaker-rabbit-start':
      startRabbitTimer({
        minutes: data.minutes,
        intervalSeconds: data.intervalSeconds,
        sprite: data.sprite,
        initiatedBy: 'speaker',
      });
      break;
    case 'annotation-draw':
      handleAnnotationDraw(data);
      break;
    case 'annotation-clear':
      clearAnnotations();
      break;
    case 'annotation-blank':
      setBlankMode(data.blankMode ?? 'off');
      break;
    case 'speaker-fullscreen':
      void setHostFullscreen(data.on === true);
      break;
    case 'speaker-goto':
      if (typeof data.page === 'number' && Number.isFinite(data.page)) {
        void jumpToPage(Math.round(data.page)).then(() => postNavigationUpdate());
      }
      break;
    default:
      break;
  }
}

function sendSpeakerPing(label: string) {
  if (!speakerWindowRef || speakerWindowRef.closed || !speakerHandshakeToken) {
    return;
  }
  const message = {
    type: 'speaker-ping',
    handshake: speakerHandshakeToken,
    label,
    timestamp: Date.now(),
  } as const;
  try {
    const origin = getSpeakerOrigin();
    speakerWindowRef.postMessage(message, origin);
  } catch (error) {
    console.warn('Failed to ping speaker window', error);
  }
}

function getSpeakerOrigin(): string {
  if (!speakerWindowRef) {
    return window.location.origin;
  }
  try {
    const href = speakerWindowRef.location?.href;
    if (href) {
      return new URL(href).origin;
    }
  } catch {
    // fall back to current origin if cross-origin access fails
  }
  return window.location.origin;
}

function sendNavigationUpdate() {
  if (!speakerWindowRef || speakerWindowRef.closed || !speakerHandshakeToken) {
    return;
  }
  const pdfDoc = controller.pdfDocument;
  if (!pdfDoc) {
    return;
  }
  const totalPages = controller.numPages;
  const currentPage = controller.pageNumber;
  const nextPage = currentPage < totalPages ? currentPage + 1 : null;

  const message = {
    type: 'speaker-navigation-update',
    handshake: speakerHandshakeToken,
    currentPage,
    nextPage,
    totalPages,
    pdfUrl: slideUrl,
  } as const;

  try {
    const origin = getSpeakerOrigin();
    speakerWindowRef.postMessage(message, origin);
  } catch (error) {
    console.warn('Failed to send navigation update to speaker window', error);
  }
}

function scheduleSpeakerViewUpdate() {
  sendNavigationUpdate();
}

function closeSpeakerWindow() {
  if (speakerWindowRef && !speakerWindowRef.closed) {
    try {
      speakerWindowRef.close();
    } catch {
      /* ignore close errors */
    }
  }
  resetSpeakerState();
  speakerWindowRef = null;
}

function resetSpeakerState(options?: { keepHandshake?: boolean }) {
  if (pendingSpeakerReadyTimeout !== null) {
    window.clearTimeout(pendingSpeakerReadyTimeout);
    pendingSpeakerReadyTimeout = null;
  }
  if (!options?.keepHandshake) {
    pendingSpeakerWindow = null;
    speakerHandshakeToken = null;
  }
  speakerChannelRegistered = false;
}

function wireResizeHandler() {
  window.addEventListener(
    'resize',
    throttle(() => {
      void controller.fitItSize();
      refreshRabbitTimerPositions();
      syncOverlaySize();
      renderOverlay();
      scheduleSpeakerViewUpdate();
    }, 100),
    { signal: eventSignal },
  );
}

function applyCanvasTouchAction() {
  controller.domMapObject.canvas.style.touchAction = 'pan-y';
}

function wireSwipeGestures() {
  if (!('PointerEvent' in window)) {
    return;
  }

  container.style.touchAction = 'pan-y';
  applyCanvasTouchAction();

  let activePointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  const swipeThreshold = 45;

  const reset = () => {
    activePointerId = null;
  };

  container.addEventListener(
    'pointerdown',
    (event: PointerEvent) => {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') {
        return;
      }
      if (activePointerId !== null) {
        return;
      }
      activePointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      if (typeof container.setPointerCapture === 'function') {
        container.setPointerCapture(event.pointerId);
      }
    },
    { signal: eventSignal },
  );

  container.addEventListener(
    'pointerup',
    (event: PointerEvent) => {
      if (event.pointerId !== activePointerId) {
        return;
      }
      if (
        typeof container.hasPointerCapture === 'function' &&
        container.hasPointerCapture(event.pointerId)
      ) {
        container.releasePointerCapture(event.pointerId);
      }
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      reset();
      if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) {
        return;
      }
      if (deltaX < 0) {
        void nextPage();
      } else {
        void prevPage();
      }
    },
    { signal: eventSignal },
  );

  const cancelHandler = (event: PointerEvent) => {
    if (event.pointerId !== activePointerId) {
      return;
    }
    if (
      typeof container.hasPointerCapture === 'function' &&
      container.hasPointerCapture(event.pointerId)
    ) {
      container.releasePointerCapture(event.pointerId);
    }
    reset();
  };

  container.addEventListener('pointercancel', cancelHandler, { signal: eventSignal });
  container.addEventListener('lostpointercapture', cancelHandler, { signal: eventSignal });
}

function setupControlsVisibility() {
  const candidate = document.querySelector('.controls');
  if (!(candidate instanceof HTMLElement)) {
    return;
  }
  controlsElement = candidate;
  controlsElement.addEventListener(
    'click',
    () => {
      showControls();
    },
    { signal: eventSignal },
  );
}

function fadeControls() {
  setControlsOpacity('0.2');
}

function showControls() {
  setControlsOpacity('1');
}

function setControlsOpacity(value: string) {
  if (!controlsElement) {
    const candidate = document.querySelector('.controls');
    if (candidate instanceof HTMLElement) {
      controlsElement = candidate;
    } else {
      return;
    }
  }
  controlsElement.style.opacity = value;
}

async function prevPage(shouldFade = true) {
  await controller.prevPage();
  postNavigationUpdate();
  if (shouldFade) {
    fadeControls();
  }
}

async function nextPage(shouldFade = true) {
  await controller.nextPage();
  postNavigationUpdate();
  if (shouldFade) {
    fadeControls();
  }
}

function postNavigationUpdate() {
  updatePageAttribute();
  updateURL();
  updateSpeakerButtonVisibility();
  updateRabbitPageProgress();
  resetTransientAnnotations();
  syncOverlaySize();
  renderOverlay();
  scheduleSpeakerViewUpdate();
  sendSpeakerPing('navigation-update');
}

function updatePageAttribute() {
  container.setAttribute('data-page', String(controller.pageNumber));
}

function showSpeakerButton() {
  const button = document.getElementById('js-speaker');
  if (!(button instanceof HTMLElement)) {
    return;
  }
  if (!isFinePointer) {
    button.style.display = 'none';
    return;
  }
  button.style.display = '';
}

function updateSpeakerButtonVisibility() {
  const button = document.getElementById('js-speaker');
  if (!(button instanceof HTMLElement)) {
    return;
  }
  if (!isFinePointer) {
    button.style.display = 'none';
    return;
  }
  const currentPage = controller.pageNumber;
  // ページ移動時は、先頭ページ（1ページ目）以外では非表示
  if (currentPage === 1) {
    button.style.display = '';
  } else {
    button.style.display = 'none';
  }
}

function updateURL() {
  const newHash = `#p=${controller.pageNumber}`;
  if (window.location.hash !== newHash) {
    window.history.replaceState(null, document.title, newHash);
  }
}

function setupRabbitTimerControls() {
  const root = document.getElementById('js-rabbit-timer');
  const track = document.getElementById('js-rabbit-track');
  const turtle = document.getElementById('js-rabbit-turtle');
  const bunny = document.getElementById('js-rabbit-bunny');
  const status = document.getElementById('js-rabbit-status');
  const elapsedFlag = document.getElementById('js-rabbit-flag-elapsed');
  const totalFlag = document.getElementById('js-rabbit-flag-total');

  if (
    !(root instanceof HTMLElement) ||
    !(track instanceof HTMLElement) ||
    !(turtle instanceof HTMLImageElement) ||
    !(bunny instanceof HTMLImageElement) ||
    !(elapsedFlag instanceof HTMLElement) ||
    !(totalFlag instanceof HTMLElement)
  ) {
    return;
  }

  rabbitTimerState.dom = {
    root,
    track,
    turtle,
    bunny,
    status: status instanceof HTMLElement ? status : null,
    elapsedFlag,
    totalFlag,
  };
  setRabbitSprite(DEFAULT_RABBIT_SPRITE);
}

function startRabbitTimer(options?: {
  minutes?: number;
  intervalSeconds?: number;
  sprite?: string | null;
  initiatedBy?: 'speaker' | 'host';
}) {
  const dom = rabbitTimerState.dom;
  if (!dom) {
    if (options?.initiatedBy === 'speaker') {
      notifySpeakerRabbitStatus('error', 'Rabbit timer UI is unavailable.');
    }
    return;
  }
  const initiatedBy: 'speaker' | 'host' = options?.initiatedBy ?? 'host';
  const minutesValue = options?.minutes ?? Number.NaN;
  if (!Number.isFinite(minutesValue) || minutesValue <= 0) {
    const message =
      initiatedBy === 'speaker'
        ? 'Invalid Rabbit timer request. Enter minutes in the speaker view.'
        : 'Provide a positive number of minutes to start the Rabbit timer.';
    setRabbitStatus(message);
    notifySpeakerRabbitStatus('error', message);
    return;
  }
  const requestedInterval = options?.intervalSeconds ?? Number.NaN;
  const intervalSeconds =
    Number.isFinite(requestedInterval) && requestedInterval > 0
      ? requestedInterval
      : DEFAULT_RABBIT_TURTLE_STEP_MS / 1000;
  const turtleStepMs = Math.max(Math.floor(intervalSeconds * 1000), 1000);
  const durationMs = Math.max(minutesValue * 60 * 1000, 1000);
  rabbitTimerState.durationMs = durationMs;
  rabbitTimerState.startTimestamp = Date.now();
  rabbitTimerState.isRunning = true;
  rabbitTimerState.hasStarted = true;
  rabbitTimerState.lastTimeRatio = -1;
  rabbitTimerState.turtleStepMs = turtleStepMs;
  rabbitTimerState.spriteType = normalizeRabbitSprite(options?.sprite);
  setRabbitSprite(rabbitTimerState.spriteType);
  dom.root.classList.add('rabbit-timer--visible');
  dom.totalFlag.textContent = formatMinutesLabel(minutesValue);
  dom.elapsedFlag.textContent = '0';

  if (rabbitTimerState.intervalId !== null) {
    window.clearInterval(rabbitTimerState.intervalId);
  }
  rabbitTimerState.intervalId = window.setInterval(() => {
    updateRabbitTimerTick();
  }, 1000);

  const minutesLabel = formatMinutesLabel(minutesValue);
  notifySpeakerRabbitStatus('running', `Rabbit timer started (${minutesLabel} min).`, {
    minutes: minutesValue,
    intervalSeconds: turtleStepMs / 1000,
    elapsedMs: 0,
    durationMs,
    sprite: rabbitTimerState.spriteType,
  });

  window.requestAnimationFrame(() => {
    updateRabbitTimerTick(true);
    updateRabbitPageProgress(true);
    refreshRabbitTimerPositions();
  });
}

function updateRabbitTimerTick(forceStatusUpdate = false) {
  if (
    !rabbitTimerState.dom ||
    !rabbitTimerState.hasStarted ||
    rabbitTimerState.startTimestamp === null
  ) {
    return;
  }
  const now = Date.now();
  const elapsedMs = Math.max(0, now - rabbitTimerState.startTimestamp);
  const cappedElapsed = Math.min(elapsedMs, rabbitTimerState.durationMs);
  const timerCompleted = elapsedMs >= rabbitTimerState.durationMs;
  rabbitTimerState.dom.elapsedFlag.textContent = formatElapsedMinutesLabel(cappedElapsed);

  updateRabbitTurtleProgress(cappedElapsed, timerCompleted);

  if (rabbitTimerState.isRunning || forceStatusUpdate || timerCompleted) {
    const totalLabel = formatDuration(rabbitTimerState.durationMs);
    let statusMessage: string | null = null;
    const extras: RabbitTimerStatusExtras = {
      minutes: rabbitTimerState.durationMs / 60000,
      intervalSeconds: rabbitTimerState.turtleStepMs / 1000,
      elapsedMs: cappedElapsed,
      durationMs: rabbitTimerState.durationMs,
      sprite: rabbitTimerState.spriteType,
    };
    if (timerCompleted && rabbitTimerState.isRunning) {
      rabbitTimerState.isRunning = false;
      statusMessage = `Time's up! ${totalLabel} elapsed.`;
      notifySpeakerRabbitStatus('completed', statusMessage, extras);
    } else if (!timerCompleted) {
      statusMessage = '';
      notifySpeakerRabbitStatus('running', '', extras);
    }
    if (statusMessage !== null) {
      setRabbitStatus(statusMessage);
    }
  }

  if (!rabbitTimerState.isRunning && rabbitTimerState.intervalId !== null) {
    window.clearInterval(rabbitTimerState.intervalId);
    rabbitTimerState.intervalId = null;
  }
}

function updateRabbitTurtleProgress(elapsedMs: number, timerCompleted: boolean) {
  const dom = rabbitTimerState.dom;
  if (!dom || rabbitTimerState.durationMs <= 0) {
    return;
  }
  const stepMs = rabbitTimerState.turtleStepMs || DEFAULT_RABBIT_TURTLE_STEP_MS;
  let ratio = 0;
  if (timerCompleted) {
    ratio = 1;
  } else if (rabbitTimerState.durationMs > 0) {
    const quantizedMs = Math.floor(elapsedMs / stepMs) * stepMs;
    ratio = Math.min(quantizedMs / rabbitTimerState.durationMs, 1);
  }
  ratio = Math.max(0, Math.min(1, ratio));
  if (!timerCompleted && ratio === rabbitTimerState.lastTimeRatio) {
    return;
  }
  rabbitTimerState.lastTimeRatio = ratio;
  setRabbitCharacterPosition(dom.turtle, ratio);
}

function updateRabbitPageProgress(force = false) {
  const dom = rabbitTimerState.dom;
  if (!dom) {
    return;
  }
  const totalPages = controller.numPages;
  const currentPage = controller.pageNumber;
  if (!totalPages || totalPages <= 0) {
    return;
  }
  const ratio = Math.max(0, Math.min(1, currentPage / totalPages));
  if (!force && ratio === rabbitTimerState.lastPageRatio) {
    return;
  }
  rabbitTimerState.lastPageRatio = ratio;
  setRabbitCharacterPosition(dom.bunny, ratio);
}

function refreshRabbitTimerPositions() {
  if (!rabbitTimerState.dom || !rabbitTimerState.hasStarted) {
    return;
  }
  if (rabbitTimerState.lastTimeRatio >= 0) {
    setRabbitCharacterPosition(
      rabbitTimerState.dom.turtle,
      Math.max(0, rabbitTimerState.lastTimeRatio),
    );
  }
  if (rabbitTimerState.lastPageRatio >= 0) {
    setRabbitCharacterPosition(
      rabbitTimerState.dom.bunny,
      Math.max(0, rabbitTimerState.lastPageRatio),
    );
  }
}

function setRabbitStatus(message: string) {
  if (!rabbitTimerState.dom || !rabbitTimerState.dom.status) {
    return;
  }
  rabbitTimerState.dom.status.textContent = message;
}

function normalizeRabbitSprite(input?: string | null): RabbitSpriteType {
  if (input && Object.prototype.hasOwnProperty.call(RABBIT_SPRITE_SOURCES, input)) {
    return input as RabbitSpriteType;
  }
  return DEFAULT_RABBIT_SPRITE;
}

function setRabbitSprite(type: RabbitSpriteType) {
  rabbitTimerState.spriteType = type;
  if (!rabbitTimerState.dom) {
    return;
  }
  const src = RABBIT_SPRITE_SOURCES[type];
  rabbitTimerState.dom.turtle.src = src;
  rabbitTimerState.dom.turtle.setAttribute('data-sprite', type);
}

function setRabbitCharacterPosition(element: HTMLElement, ratio: number) {
  const dom = rabbitTimerState.dom;
  if (!dom) {
    return;
  }
  const trackWidth = dom.track.clientWidth;
  if (trackWidth <= 0) {
    return;
  }
  const elementWidth =
    element.clientWidth || (element instanceof HTMLImageElement ? element.naturalWidth : 0) || 0;
  const availableWidth = Math.max(trackWidth - elementWidth, 0);
  const clampedRatio = Math.max(0, Math.min(1, ratio));
  const offset = availableWidth * clampedRatio;
  element.style.transform = `translate3d(${offset}px, 0, 0)`;
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) {
    return '0:00';
  }
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatMinutesLabel(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }
  return Number.isInteger(value) ? value.toString() : value.toFixed(1);
}

function formatElapsedMinutesLabel(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) {
    return '0';
  }
  return Math.floor(ms / 60000).toString();
}

function notifySpeakerRabbitStatus(
  state: RabbitTimerSpeakerState,
  message: string,
  extra?: RabbitTimerStatusExtras,
) {
  if (!speakerWindowRef || speakerWindowRef.closed || !speakerHandshakeToken) {
    return;
  }
  try {
    const origin = getSpeakerOrigin();
    speakerWindowRef.postMessage(
      {
        type: 'rabbit-timer-status',
        handshake: speakerHandshakeToken,
        state,
        message,
        minutes: extra?.minutes,
        intervalSeconds: extra?.intervalSeconds,
        elapsedMs: extra?.elapsedMs,
        durationMs: extra?.durationMs,
        sprite: extra?.sprite,
      },
      origin,
    );
  } catch (error) {
    console.warn('Failed to share Rabbit timer status with speaker view', error);
  }
}

function setupAnnotationOverlay(): void {
  if (annotationState.overlay) {
    return;
  }
  const overlay = document.createElement('canvas');
  overlay.id = 'js-annotation-overlay';
  overlay.style.position = 'absolute';
  overlay.style.left = '0';
  overlay.style.top = '0';
  overlay.style.pointerEvents = 'none';
  overlay.style.zIndex = '5';
  container.appendChild(overlay);
  annotationState.overlay = overlay;
  annotationState.ctx = overlay.getContext('2d');

  const blank = document.createElement('div');
  blank.id = 'js-blank-screen';
  blank.style.position = 'fixed';
  blank.style.inset = '0';
  blank.style.zIndex = '9999';
  blank.style.display = 'none';
  document.body.appendChild(blank);
  annotationState.blankLayer = blank;

  container.addEventListener(
    Events.page_rendered,
    () => {
      applyCanvasTouchAction();
      syncOverlaySize();
      renderOverlay();
    },
    { signal: eventSignal },
  );

  syncOverlaySize();
  renderOverlay();
}

function syncOverlaySize(): void {
  const overlay = annotationState.overlay;
  const base = controller.domMapObject.canvas;
  if (!overlay) {
    return;
  }
  const baseRect = base.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const cssWidth = baseRect.width || base.clientWidth;
  const cssHeight = baseRect.height || base.clientHeight;
  if (cssWidth <= 0 || cssHeight <= 0) {
    return;
  }
  const dpr = window.devicePixelRatio || 1;
  overlay.style.left = `${baseRect.left - containerRect.left}px`;
  overlay.style.top = `${baseRect.top - containerRect.top}px`;
  overlay.style.width = `${cssWidth}px`;
  overlay.style.height = `${cssHeight}px`;
  overlay.width = Math.max(1, Math.round(cssWidth * dpr));
  overlay.height = Math.max(1, Math.round(cssHeight * dpr));
}

function ensureOverlayRaf(): void {
  if (annotationState.rafId !== null) {
    return;
  }
  annotationState.rafId = window.requestAnimationFrame(() => {
    annotationState.rafId = null;
    renderOverlay();
    if (annotationState.laserTrail.length > 0) {
      ensureOverlayRaf();
    }
  });
}

function renderOverlay(): void {
  const ctx = annotationState.ctx;
  const overlay = annotationState.overlay;
  if (!ctx || !overlay) {
    return;
  }
  const w = overlay.width;
  const h = overlay.height;
  ctx.clearRect(0, 0, w, h);

  const page = controller.pageNumber;
  const strokes = annotationState.strokesByPage.get(page);
  if (strokes) {
    for (const stroke of strokes) {
      drawInkStroke(ctx, stroke, w, h);
    }
  }
  if (annotationState.activeStroke) {
    drawInkStroke(ctx, annotationState.activeStroke, w, h);
  }
  if (annotationState.spotlight) {
    drawSpotlight(ctx, annotationState.spotlight, w, h);
  }
  drawAudienceLaser(ctx, w, h);
}

function drawInkStroke(
  ctx: CanvasRenderingContext2D,
  stroke: InkStroke,
  w: number,
  h: number,
): void {
  const pts = stroke.points;
  if (pts.length === 0) {
    return;
  }
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.strokeStyle = stroke.color;
  ctx.fillStyle = stroke.color;
  const lineWidth = Math.max(stroke.size * w, 1);
  ctx.lineWidth = lineWidth;
  if (stroke.tool === 'highlighter') {
    ctx.globalAlpha = 0.38;
    ctx.globalCompositeOperation = 'multiply';
  }
  if (pts.length === 1) {
    ctx.beginPath();
    ctx.arc(pts[0].x * w, pts[0].y * h, lineWidth / 2, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.beginPath();
    pts.forEach((p, index) => {
      const x = p.x * w;
      const y = p.y * h;
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();
  }
  ctx.restore();
}

function drawSpotlight(
  ctx: CanvasRenderingContext2D,
  point: NormPoint,
  w: number,
  h: number,
): void {
  const radius = SPOTLIGHT_RADIUS_RATIO * Math.hypot(w, h);
  const cx = point.x * w;
  const cy = point.y * h;
  ctx.save();
  ctx.fillStyle = `rgba(0,0,0,${SPOTLIGHT_DIM})`;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'destination-out';
  const gradient = ctx.createRadialGradient(cx, cy, radius * 0.55, cx, cy, radius);
  gradient.addColorStop(0, 'rgba(0,0,0,1)');
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawAudienceLaser(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const now = Date.now();
  annotationState.laserTrail = pruneLaserTrail(annotationState.laserTrail, now);
  drawLaserTrail(ctx, annotationState.laserTrail, w, h, now);
}

function handleAnnotationDraw(data: AnnotationDrawMessage): void {
  const tool = data.tool;
  const x = typeof data.x === 'number' ? data.x : 0;
  const y = typeof data.y === 'number' ? data.y : 0;

  if (tool === 'laser') {
    annotationState.laserTrail.push({ x, y, t: Date.now() });
    renderOverlay();
    ensureOverlayRaf();
    return;
  }

  if (tool === 'spotlight') {
    annotationState.spotlight = data.phase === 'end' ? null : { x, y };
    renderOverlay();
    return;
  }

  if (tool === 'pen' || tool === 'highlighter') {
    if (data.phase === 'start') {
      annotationState.activeStroke = {
        tool,
        color: data.color || DEFAULT_PEN_COLOR,
        size:
          typeof data.size === 'number'
            ? data.size
            : tool === 'highlighter'
              ? DEFAULT_HIGHLIGHTER_SIZE
              : DEFAULT_PEN_SIZE,
        points: [{ x, y }],
      };
    } else if (data.phase === 'move') {
      annotationState.activeStroke?.points.push({ x, y });
    } else if (data.phase === 'end') {
      const stroke = annotationState.activeStroke;
      if (stroke && stroke.points.length > 0) {
        const page = controller.pageNumber;
        const list = annotationState.strokesByPage.get(page) ?? [];
        list.push(stroke);
        annotationState.strokesByPage.set(page, list);
      }
      annotationState.activeStroke = null;
    }
    renderOverlay();
  }
}

function clearAnnotations(): void {
  const page = controller.pageNumber;
  annotationState.strokesByPage.delete(page);
  annotationState.activeStroke = null;
  annotationState.laserTrail = [];
  annotationState.spotlight = null;
  renderOverlay();
}

function resetTransientAnnotations(): void {
  annotationState.activeStroke = null;
  annotationState.laserTrail = [];
  annotationState.spotlight = null;
}

function setBlankMode(mode: BlankMode): void {
  const layer = annotationState.blankLayer;
  if (!layer) {
    return;
  }
  const normalized: BlankMode = mode === 'black' || mode === 'white' ? mode : 'off';
  annotationState.blankMode = normalized;
  if (normalized === 'off') {
    layer.style.display = 'none';
    layer.style.background = '';
  } else {
    layer.style.display = 'block';
    layer.style.background = normalized === 'black' ? '#000' : '#fff';
  }
}
